import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Title,
  Paper,
  Group,
  Text,
  Table,
  ActionIcon,
  Modal,
  Button,
  Divider,
  Box,
  Tooltip,
  Popover,
  LoadingOverlay,
  useMantineTheme,
  Alert,
} from '@mantine/core';
import {
  IconPlus,
  IconDownload,
  IconTrash,
  IconFile,
  IconPhoto,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import FileUploadList from '../FileUploadList';
import { FileUploadItem, ExistingFileInfo } from '../types/ObjectFormTypes';
import { UseFormReturnType } from '@mantine/form';
import { ObjectData } from '../types/ObjectFormTypes';
import useTransientStore from '../../../data/transcientStore';
import * as CityObjectAttachmentsService from '../../../services/CityObjectAttachmentsService';
import usePermissions from '../../../hooks/usePermissions';
import { useTranslation } from "../../../hooks/useTranslation";

interface MeasurementsTabProps {
  form: UseFormReturnType<ObjectData>;
  canModify?: boolean;
}

interface AttachmentFile extends ExistingFileInfo {
  uploadDate: Date;
}

export default function MeasurementsTab({
  form,
  canModify = true,
}: MeasurementsTabProps) {
  // Get current user info from store
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 345, addressSystemId: addrIdFromStore || 0, autoLoad: true });
  const theme = useMantineTheme();
  const headerColor = theme.colors.gray[2];
  const headerText = theme.colors.dark[7];

  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [uploadModalOpened, setUploadModalOpened] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<FileUploadItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);

  // Get objectId and contractId from form values
  const objectId = form.values.id || 0;
  const contractId = parseInt(form.values.job) || 0;

  // Get permission for viewing/modifying measurement attachments
  const { checkAccess } = usePermissions();
  const canViewAttachments = checkAccess('CITY_OBJECT_ATTACHMENT_SHOW');
  const canModifyAttachments = checkAccess('CITY_OBJECT_ATTACHMENT_MODIFY');

  const fetchAttachments = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await CityObjectAttachmentsService.getAttachments(
        objectId,
        contractId,
        currentUserId,
        selectedAddressSystem.id
      );

      // Transform backend data to match our interface
      const transformedAttachments: AttachmentFile[] = data.map((item) => ({
        id: item.id,
        name: item.originalName,
        size: item.size,
        extension: item.originalName.split('.').pop() || '',
        fileHeader: item.fileHeader,
        fileType: 0, // Measurement attachments
        hasThumbnail: false,
        uploadDate: new Date(item.created),
      }));

      setAttachments(transformedAttachments);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      notifications.show({
        title: getTranslation("TITLE_ERROR") || 'Error',
        message:
          getTranslation("MESSAGE_MEASUREMENT_ATTACHMENTS_LOAD_FAILED") ||
          'Failed to load measurement attachments',
        color: 'red',
        autoClose: 5000,
        position: 'top-center',
        classNames: {
          root: 'notification-error',
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [objectId, contractId, currentUserId, selectedAddressSystem]);

  // Fetch existing attachments when component mounts or objectId changes
  useEffect(() => {
    if (objectId > 0 && contractId > 0) {
      fetchAttachments();
    }
  }, [objectId, contractId, fetchAttachments]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('fi-FI', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleSaveFiles = async () => {
    // Validate that all files have headers
    const filesWithoutHeaders = pendingFiles.filter(
      (f) => !f.fileHeader || f.fileHeader.trim() === ''
    );

    if (filesWithoutHeaders.length > 0) {
      notifications.show({
        title: getTranslation("TITLE_ERROR") || 'Error',
        message:
          getTranslation("MESSAGE_FILE_HEADER_REQUIRED") ||
          'Please add a file header for all files before uploading',
        color: 'red',
        autoClose: 5000,
        position: 'top-center',
        classNames: {
          root: 'notification-error',
        },
      });
      return;
    }

    setIsSaving(true);

    try {
      // Convert files to base64 and create upload requests
      const uploadPromises = pendingFiles.map(async (fileItem) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(fileItem.file);
        }).then((base64Data) => ({
          fileName: fileItem.file.name,
          fileHeader: fileItem.fileHeader,
          fileData: base64Data,
          contentType: fileItem.file.type,
          fileType: 0, // Measurement attachment type
        }));
      });

      const uploadRequests = await Promise.all(uploadPromises);

      // Upload files to backend
      await CityObjectAttachmentsService.uploadAttachments({
        perceptionInfoId: objectId,
        contractId: contractId,
        userId: currentUserId,
        files: uploadRequests,
      });

      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || 'Success',
        message:
          getTranslation("MESSAGE_FILES_UPLOADED") ||
          `${pendingFiles.length} file(s) uploaded successfully`,
        color: 'green',
        autoClose: 5000,
        position: 'top-center',
        classNames: {
          root: 'notification-success',
        },
      });

      // Refresh the attachments list
      await fetchAttachments();

      // Clear pending files and close modal
      setPendingFiles([]);
      setUploadModalOpened(false);
    } catch (error) {
      console.error('Error uploading files:', error);
      notifications.show({
        title: getTranslation("TITLE_UPLOAD_ERROR") || 'Upload Error',
        message:
          getTranslation("MESSAGE_UPLOAD_FILES_FAILED") ||
          'Failed to upload files. Please try again.',
        color: 'red',
        autoClose: 5000,
        position: 'top-center',
        classNames: {
          root: 'notification-error',
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = async (attachment: AttachmentFile) => {
    try {
      const fileData = await CityObjectAttachmentsService.downloadAttachment(
        attachment.id,
        contractId,
        objectId,
        currentUserId,
        selectedAddressSystem.id
      );

      // Create download link
      const url = window.URL.createObjectURL(fileData.content);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || 'Success',
        message:
          getTranslation("MESSAGE_FILE_DOWNLOADED") ||
          'File downloaded successfully',
        color: 'green',
        autoClose: 5000,
        position: 'top-center',
        classNames: {
          root: 'notification-success',
        },
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      notifications.show({
        title: getTranslation("TITLE_DOWNLOAD_ERROR") || 'Download Error',
        message:
          getTranslation("MESSAGE_DOWNLOAD_FILE_FAILED") ||
          'Failed to download file',
        color: 'red',
        autoClose: 5000,
        position: 'top-center',
        classNames: {
          root: 'notification-error',
        },
      });
    }
  };

  const handleDelete = async (attachment: AttachmentFile) => {
    setDeletingAttachmentId(attachment.id);
  };

  const performDelete = async (attachment: AttachmentFile) => {
    try {
      await CityObjectAttachmentsService.deleteAttachment(
        attachment.id,
        contractId,
        objectId,
        currentUserId,
        selectedAddressSystem.id
      );

      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || 'Success',
        message:
          getTranslation("MESSAGE_FILE_DELETED") ||
          'File deleted successfully',
        color: 'green',
        autoClose: 5000,
        position: 'top-center',
        classNames: {
          root: 'notification-success',
        },
      });

      // Refresh the attachments list
      await fetchAttachments();
      setDeletingAttachmentId(null);
    } catch (error) {
      console.error('Error deleting file:', error);
      notifications.show({
        title: getTranslation("TITLE_DELETE_ERROR") || 'Delete Error',
        message:
          getTranslation("MESSAGE_DELETE_FILE_FAILED") ||
          'Failed to delete file',
        color: 'red',
        autoClose: 5000,
        position: 'top-center',
        classNames: {
          root: 'notification-error',
        },
      });
      setDeletingAttachmentId(null);
    }
  };

  const closeModal = () => {
    setUploadModalOpened(false);
    setPendingFiles([]);
  };

  const getFileIcon = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase();
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
    if (ext && imageExts.includes(ext)) {
      return <IconPhoto size={16} />;
    }
    return <IconFile size={16} />;
  };

  return (
    <>
      <Stack gap="lg" style={{ position: 'relative' }}>
        <LoadingOverlay 
          visible={isLoading || isSaving} 
          zIndex={1000}
          overlayProps={{ blur: 2 }}
        />
        <Group justify="space-between" align="center">
          <Title order={4} c="blue">
            {getTranslation("TITLE_MEASUREMENT_ATTACHMENTS") ||
              "Measurement Protocol Attachments"}
          </Title>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setUploadModalOpened(true)}
            size="sm"
            disabled={!canModifyAttachments || !canModify || !canViewAttachments}
          >
            {getTranslation("LABEL_ADD_NEW") || "Add New"}
          </Button>
        </Group>

        {/* Measurement Protocol Attachments Table */}
        <Paper withBorder>
          <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: headerColor, color: headerText }}>
                  <Table.Th style={{ color: headerText }}>
                    {getTranslation("LABEL_TITLE") || "Title"}
                  </Table.Th>
                  <Table.Th style={{ color: headerText, borderLeft: '1px solid #ddd' }}>
                    {getTranslation("LABEL_DATE") || "Date"}
                  </Table.Th>
                  <Table.Th style={{ color: headerText, borderLeft: '1px solid #ddd' }}>
                    {getTranslation("LABEL_SIZE") || "Size"}
                  </Table.Th>
                  <Table.Th style={{ color: headerText, borderLeft: '1px solid #ddd', textAlign: 'center' }}>
                    {getTranslation("LABEL_ACTIONS") || "Actions"}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {attachments.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Stack align="center" gap="xs" py="xl">
                        <Text c="blue" size="sm" fw={500}>
                          {getTranslation("LABEL_NO_MEASUREMENT_ATTACHMENTS") ||
                            "No measurement attachments yet"}
                        </Text>
                        <Text c="dimmed" size="xs">
                          {getTranslation("LABEL_CLICK_ADD_NEW_UPLOAD") ||
                            "Click \"Add New\" to upload files"}
                        </Text>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  attachments.map((attachment) => (
                    <Table.Tr key={attachment.id}>
                      <Table.Td>
                        <Group gap="xs">
                          {getFileIcon(attachment.name)}
                          <Box>
                            <Tooltip label={attachment.name}>
                              <Text size="sm" fw={500} lineClamp={1}>
                                {attachment.fileHeader || attachment.name}
                              </Text>
                            </Tooltip>
                            {attachment.fileHeader && (
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {attachment.name}
                              </Text>
                            )}
                          </Box>
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ borderLeft: '1px solid #ddd' }}>
                        <Text size="sm">{formatDate(attachment.uploadDate)}</Text>
                      </Table.Td>
                      <Table.Td style={{ borderLeft: '1px solid #ddd' }}>
                        <Text size="sm">{formatFileSize(attachment.size)}</Text>
                      </Table.Td>
                      <Table.Td style={{ borderLeft: '1px solid #ddd', textAlign: 'center' }}>
                        <Group gap="xs" justify="center">
                          <Tooltip 
                            label={getTranslation("LABEL_DOWNLOAD") || "Download"}
                            withArrow
                          >
                            <ActionIcon
                              variant="subtle"
                              color="blue"
                              onClick={() => handleDownload(attachment)}
                              disabled={!canModifyAttachments || !canModify}
                            >
                              <IconDownload size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Popover
                            position="bottom"
                            withArrow
                            shadow="md"
                            opened={deletingAttachmentId === attachment.id}
                            onClose={() => setDeletingAttachmentId(null)}
                          >
                            <Popover.Target>
                              <Tooltip 
                                label={getTranslation("LABEL_DELETE") || "Delete"}
                                withArrow
                              >
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  onClick={() => handleDelete(attachment)}
                                  disabled={!canModifyAttachments || !canModify}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Popover.Target>
                            <Popover.Dropdown>
                              <Group gap="xs">
                                <Text size="xs" fw={500}>
                                  {getTranslation("LABEL_CONFIRM_SHORT") ||
                                    "Sure?"}
                                </Text>
                                <Button
                                  size="xs"
                                  color="red"
                                  variant="filled"
                                  onClick={() => performDelete(attachment)}
                                >
                                  {getTranslation("LABEL_YES") || "Yes"}
                                </Button>
                                <Button
                                  size="xs"
                                  variant="default"
                                  onClick={() => setDeletingAttachmentId(null)}
                                >
                                  {getTranslation("LABEL_NO") || "No"}
                                </Button>
                              </Group>
                            </Popover.Dropdown>
                          </Popover>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>

      {/* File Upload Modal */}
      <Modal
        opened={uploadModalOpened}
        onClose={closeModal}
        title={getTranslation("TITLE_ADD_MEASUREMENT_ATTACHMENTS") || "Add Measurement Attachments"}
        size="lg"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {getTranslation("LABEL_UPLOAD_MEASUREMENT_WITH_HEADER") ||
              "Upload measurement protocol files with a file header."}
          </Text>

          <FileUploadList
            files={pendingFiles}
            onChange={setPendingFiles}
            label={getTranslation("LABEL_FILES") || "Files"}
            description={
              getTranslation("LABEL_ADD_FILES_TO_UPLOAD") ||
              "Add files to upload"
            }
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            maxFileSize={30 * 1024 * 1024} // 30MB
          />

          <Divider />

          <Group justify="flex-end">
            <Button variant="outline" onClick={closeModal}>
              {getTranslation("LABEL_CANCEL") || "Cancel"}
            </Button>
            <Button
              onClick={handleSaveFiles}
              disabled={pendingFiles.length === 0}
              loading={isSaving}
            >
              {getTranslation("LABEL_UPLOAD") || "Upload"}{" "}
              {pendingFiles.length > 0 && `(${pendingFiles.length})`}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
